import classNames from "classnames";
import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { useState } from "Shared/haunted/CustomHooks";
import { useReduxState } from "Shared/redux/useReduxState";
import { TravelInformationContents, TravelInfoSubsection, TravelInfoWidget } from "./KenticoKontent";

export type TabName = "people" | "corporate-portal";

export const useTravelInformation = () => {
	const [personsKontent] = useReduxState("kontent.travelInformation.travelInformationPersonas.contents");
	const [portalKontent] = useReduxState("kontent.travelInformation.travelInformationCorp.contents");

	const [selectedTabName, setSelectedTabName] = useState<TabName>("people");

	const tabTemplate = (name: TabName, title: string) => html`
		<div
			class=${classNames("text-xl rounded-lg px-4 py-3 basis-1/2 cursor-pointer text-center", {
				"bg-n-blue text-white font-bold": selectedTabName === name,
				"bg-gray-300 text-gray-500 font-normal": selectedTabName !== name,
			})}
			@click=${() => setSelectedTabName(name)}
		>
			${unsafeHTML(title)}
		</div>
	`;

	const tabHeaderTemplate = () => html`
		<div class=${classNames("flex w-full rounded-xl bg-gray-100 gap-4 px-2 py-4")}>
			${tabTemplate("people", personsKontent.sectionTitle.value)}
			${tabTemplate("corporate-portal", portalKontent.sectionTitle.value)}
		</div>
	`;

	const listItem = (icon: string, text: string) =>
		html`
			<li class="flex items-center gap-x-2">
				<div class="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0F3F7] text-[13px] text-n-blue">
					${unsafeHTML(icon)}
				</div>
				<div class="text-base text-n-blue">${unsafeHTML(text)}</div>
			</li>
		`;

	const subsectionTemplate = (subsection: TravelInfoSubsection) => html`
		<div>
			<div class="mb-2 text-xl/none font-bold text-n-blue">${unsafeHTML(subsection.title.value)}</div>
			<ul class="flex flex-col gap-y-3">
				${subsection.modules.contents.map((module) =>
					listItem(module.itemIcon.value, module.itemContent.value)
				)}
			</ul>
		</div>
	`;

	const widgetTemplate = (widget: TravelInfoWidget) => html`
		<div
			class="h-full w-full rounded-xl bg-cover bg-center"
			style=${`background-image: url(${widget.banner.urls[0]})`}
		>
			${unsafeHTML(widget.value)}
		</div>
	`;

	const tabContentTemplate = (contents: TravelInformationContents) => html`
		<div class="flex h-[330px] w-full gap-x-8">
			<div
				class="grid gap-y-2.5"
				style=${`grid-template-columns: repeat(${Math.ceil(contents.widget.length / 2)}, minmax(0, 1fr))`}
			>
				${contents.widget.map(widgetTemplate)}
			</div>
			<div
				class="grid w-full gap-x-8"
				style=${`grid-template-columns: repeat(${contents.subsection.length}, minmax(0, 1fr))`}
			>
				${contents.subsection.map(subsectionTemplate)}
			</div>
		</div>
	`;

	const peopleTabContentTemplate = () => (selectedTabName === "people" ? tabContentTemplate(personsKontent) : "");

	const corporatePortalContentTemplate = () =>
		selectedTabName === "corporate-portal" ? tabContentTemplate(portalKontent) : "";

	const htmlTemplate = () =>
		personsKontent && portalKontent
			? html`
					<div class="container flex h-full w-full flex-col items-center gap-6 py-8 text-left">
						${tabHeaderTemplate()} ${peopleTabContentTemplate()} ${corporatePortalContentTemplate()}
					</div>
			  `
			: "";

	return { htmlTemplate };
};
